import { useMemo } from 'react';
import useSWR, { mutate } from 'swr';

import { FieldOption, parseOptionalFieldDefinitions } from 'api/fieldDefinitions';
import axiosServices, { fetcher } from 'utils/axios';
import useTenantScopedKey from 'hooks/useTenantScopedKey';

// Re-exported so callers that already reach for the field shape through the site settings keep
// working; `api/fieldDefinitions` is the definition, this is just the doorway they came in by.
export { FIELD_TYPE, FIELD_USAGE } from 'api/fieldDefinitions';
export type { FieldOption, FieldType, FieldUsage } from 'api/fieldDefinitions';

// Mirrors Weightrax.Domain.Models.WeightDisplaySettings
export interface WeightDisplaySettings {
  unitSymbol: string;
  toKgFactor: number;
}

// Mirrors Weightrax.Domain.Enums.DirectionEnum — 1 In, 2 Out, 3 Both.
export type ManualWeightDirection = 1 | 2 | 3;

// Mirrors Weightrax.Domain.Models.WeightEntryOptions. Each is the direction manual weights may be keyed
// in for that app mode, or null to never allow them.
export interface WeightEntryOptions {
  posAllowManualWeights: ManualWeightDirection | null;
  kioskAllowManualWeights: ManualWeightDirection | null;
  tabletAllowManualWeights: ManualWeightDirection | null;
}

// Mirrors Weightrax.Domain.Models.EntitySyncSetting
export interface EntitySyncSetting {
  entityType: string;
  pollingInterval: number;
  pageSize: number;
  pushBatchSize: number;
  maxPageSize: number | null;
}

// Mirrors Weightrax.Domain.Models.VehicleTrackingSettings
export interface VehicleTrackingSettings {
  /**
   * The master switch. Off stops tracking on every device at the site — the weighbridge worker
   * re-reads this on its own poll, so switching it either way takes effect without a restart.
   */
  useVehicleTracking: boolean;
  integrateWithW1: boolean;
  w1BaseUrl: string;
  weightraxBaseUrl: string;
  docketLinkExpiryDays: number;
  /** Seconds the kiosk QR code page waits after a departure before showing that vehicle's docket. */
  secondsBeforeShowingDocket: number;
  /** Seconds the kiosk QR code page shows the docket before going back to waiting for a departure. */
  secondsToShowDocket: number;
  useANPR: boolean;
  arrivalThresholdKg: number;
  stableWeightVariationKg: number;
  stableReadingCount: number;
  axleWeightVariationKg: number;
  secondsToWaitBetweenAxleChanges: number;
  secondsToWaitForDeparture: number;
  /** Seconds a plate read waits for its vehicle to get on the bridge before it is discarded. 0 keeps it until replaced. */
  secondsToHoldPlate: number;
  addTotalToAxleWeights: boolean;
  axleGroupTotalLabel: string;
}

// Mirrors Weightrax.Domain.Models.DeviceDefaults
export interface DeviceDefaults {
  autoApprove: boolean;
}

// Mirrors Weightrax.Domain.Models.SecuritySettings
export interface SecuritySettings {
  minPinLength: number;
  maxPinLength: number;
}

// Mirrors Weightrax.Domain.Models.TimeKeeperSettings
export interface TimeKeeperSettings {
  checkIntervalMinutes: number;
  ntpServers: string[];
}

// Mirrors Weightrax.Domain.Models.KeyboardSettings. Each row is a template string where every
// character is its own key and bracketed tokens map to special keys: {SHIFT}, {BKSPC}, {DEL}, {SPC}.
export interface KeyboardSettings {
  rows: string[];
}

// Mirrors Weightrax.Domain.Models.TemperatureAlertSettings.
export interface TemperatureAlertSettings {
  /**
   * Recipients for this site's temperature alerts and daily reports. Empty inherits the fallback
   * address from Application Settings, which is shared across every tenant — so this is the only
   * place a customer's own address can be set.
   */
  alertEmailTo: string | null;
}

// Mirrors Weightrax.Domain.Models.SiteSettings, deserialized from the Site.Settings JSON column
export interface SiteSettings {
  dateFormat: string;
  timeFormat: string;
  timezone: string;
  contactEmail: string | null;
  currencySymbol: string;
  cashRoundingAmt: number;
  materialSurchargeLabel: string;
  weightDisplaySettings: WeightDisplaySettings;
  weightEntryOptions: WeightEntryOptions;
  /** Allows sundry items on the visit screen. When off, the sundry item and fuel surcharge options are hidden on the material page. */
  useSundryItems: boolean;
  syncSettings: EntitySyncSetting[];
  vehicleTrackingSettings: VehicleTrackingSettings;
  deviceDefaults: DeviceDefaults;
  securitySettings: SecuritySettings;
  timeKeeperSettings: TimeKeeperSettings;
  keyboardSettings: KeyboardSettings;
  /**
   * Null where the site inherits the company's list. An array is the site's own override, and
   * replaces the company's outright rather than being merged into it field by field.
   */
  visitFieldDefinitions: FieldOption[] | null;
  vehicleFieldDefinitions: FieldOption[] | null;
  materialFieldDefinitions: FieldOption[] | null;
  temperatureAlertSettings: TemperatureAlertSettings;
  docketPrefix: string;
  gtnWeighMaterialId: number | null;
  axleWeighMaterialId: number | null;
  defaultCustomerId: number | null;
}

// Mirrors the defaults from Weightrax.Domain.Constants.GeneralConstants / SyncDefaults
export function createDefaultSiteSettings(): SiteSettings {
  return {
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HH:mm',
    timezone: 'Pacific/Auckland',
    contactEmail: null,
    currencySymbol: '$',
    cashRoundingAmt: 0.1,
    materialSurchargeLabel: 'Surcharge',
    weightDisplaySettings: { unitSymbol: 'kg', toKgFactor: 1 },
    weightEntryOptions: { posAllowManualWeights: null, kioskAllowManualWeights: null, tabletAllowManualWeights: null },
    useSundryItems: false,
    syncSettings: [{ entityType: '', pollingInterval: 60, pageSize: 50, pushBatchSize: 20, maxPageSize: null }],
    vehicleTrackingSettings: {
      useVehicleTracking: false,
      integrateWithW1: false,
      w1BaseUrl: 'https://app.weightrax.com',
      weightraxBaseUrl: 'https://weightrax.app',
      docketLinkExpiryDays: 90,
      secondsBeforeShowingDocket: 20,
      secondsToShowDocket: 60,
      useANPR: false,
      arrivalThresholdKg: 250,
      stableWeightVariationKg: 20,
      stableReadingCount: 4,
      axleWeightVariationKg: 200,
      secondsToWaitBetweenAxleChanges: 3,
      secondsToWaitForDeparture: 6,
      secondsToHoldPlate: 60,
      addTotalToAxleWeights: false,
      axleGroupTotalLabel: 'Axle Group Total'
    },
    deviceDefaults: { autoApprove: true },
    securitySettings: { minPinLength: 4, maxPinLength: 6 },
    timeKeeperSettings: { checkIntervalMinutes: 60, ntpServers: ['time.windows.com', 'pool.ntp.org'] },
    keyboardSettings: { rows: ['1234567890{BKSPC}', 'QWERTYUIOP', '{SHIFT}ASDFGHJKL{DEL}', '.#-/_{SPC}'] },
    visitFieldDefinitions: null,
    vehicleFieldDefinitions: null,
    materialFieldDefinitions: null,
    temperatureAlertSettings: { alertEmailTo: null },
    docketPrefix: '',
    gtnWeighMaterialId: null,
    axleWeighMaterialId: null,
    defaultCustomerId: null
  };
}

const MANUAL_WEIGHT_DIRECTION_NAMES: Record<string, ManualWeightDirection> = { in: 1, out: 2, both: 3 };

// Reads a DirectionEnum written by number or by name. Anything else, null included, never allows manual weights.
function parseManualWeightDirection(value: unknown): ManualWeightDirection | null {
  if (value === 1 || value === 2 || value === 3) return value;
  if (typeof value === 'string') return MANUAL_WEIGHT_DIRECTION_NAMES[value.trim().toLowerCase()] ?? null;
  return null;
}

// Accepts either casing: the serializer writes POSAllowManualWeights as posAllowManualWeights, but
// settings JSON stored before a naming policy applied carries the C# names.
function parseWeightEntryOptions(raw: unknown): WeightEntryOptions {
  const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    posAllowManualWeights: parseManualWeightDirection(source.posAllowManualWeights ?? source.POSAllowManualWeights),
    kioskAllowManualWeights: parseManualWeightDirection(source.kioskAllowManualWeights ?? source.KioskAllowManualWeights),
    tabletAllowManualWeights: parseManualWeightDirection(source.tabletAllowManualWeights ?? source.TabletAllowManualWeights)
  };
}

function parseSiteSettings(raw: unknown): SiteSettings {
  const defaults = createDefaultSiteSettings();
  if (!raw || typeof raw !== 'object') return defaults;

  const source = raw as Record<string, unknown>;
  const weightDisplayRaw = source.weightDisplaySettings as Record<string, unknown> | undefined;
  const vehicleTrackingRaw = source.vehicleTrackingSettings as Record<string, unknown> | undefined;
  const deviceDefaultsRaw = source.deviceDefaults as Record<string, unknown> | undefined;
  const securitySettingsRaw = source.securitySettings as Record<string, unknown> | undefined;
  const timeKeeperSettingsRaw = source.timeKeeperSettings as Record<string, unknown> | undefined;
  const keyboardSettingsRaw = source.keyboardSettings as Record<string, unknown> | undefined;
  const temperatureAlertRaw = source.temperatureAlertSettings as Record<string, unknown> | undefined;
  const syncSettingsRaw = source.syncSettings;
  const ntpServersRaw = timeKeeperSettingsRaw?.ntpServers;

  return {
    dateFormat: typeof source.dateFormat === 'string' && source.dateFormat ? source.dateFormat : defaults.dateFormat,
    timeFormat: typeof source.timeFormat === 'string' && source.timeFormat ? source.timeFormat : defaults.timeFormat,
    timezone: typeof source.timezone === 'string' && source.timezone ? source.timezone : defaults.timezone,
    contactEmail: typeof source.contactEmail === 'string' ? source.contactEmail : defaults.contactEmail,
    currencySymbol: typeof source.currencySymbol === 'string' && source.currencySymbol ? source.currencySymbol : defaults.currencySymbol,
    cashRoundingAmt: typeof source.cashRoundingAmt === 'number' ? source.cashRoundingAmt : defaults.cashRoundingAmt,
    materialSurchargeLabel:
      typeof source.materialSurchargeLabel === 'string' && source.materialSurchargeLabel
        ? source.materialSurchargeLabel
        : defaults.materialSurchargeLabel,
    weightDisplaySettings: {
      unitSymbol:
        typeof weightDisplayRaw?.unitSymbol === 'string' && weightDisplayRaw.unitSymbol
          ? weightDisplayRaw.unitSymbol
          : defaults.weightDisplaySettings.unitSymbol,
      toKgFactor: typeof weightDisplayRaw?.toKgFactor === 'number' ? weightDisplayRaw.toKgFactor : defaults.weightDisplaySettings.toKgFactor
    },
    weightEntryOptions: parseWeightEntryOptions(source.weightEntryOptions ?? source.WeightEntryOptions),
    useSundryItems: Boolean(source.useSundryItems ?? defaults.useSundryItems),
    syncSettings:
      Array.isArray(syncSettingsRaw) && syncSettingsRaw.length > 0
        ? syncSettingsRaw.map((item) => {
            const settingRaw = (item ?? {}) as Record<string, unknown>;
            return {
              entityType: typeof settingRaw.entityType === 'string' ? settingRaw.entityType : '',
              pollingInterval: typeof settingRaw.pollingInterval === 'number' ? settingRaw.pollingInterval : 60,
              pageSize: typeof settingRaw.pageSize === 'number' ? settingRaw.pageSize : 50,
              pushBatchSize: typeof settingRaw.pushBatchSize === 'number' ? settingRaw.pushBatchSize : 20,
              maxPageSize: typeof settingRaw.maxPageSize === 'number' ? settingRaw.maxPageSize : null
            };
          })
        : defaults.syncSettings,
    vehicleTrackingSettings: {
      useVehicleTracking: Boolean(vehicleTrackingRaw?.useVehicleTracking ?? defaults.vehicleTrackingSettings.useVehicleTracking),
      integrateWithW1: Boolean(vehicleTrackingRaw?.integrateWithW1 ?? defaults.vehicleTrackingSettings.integrateWithW1),
      w1BaseUrl:
        typeof vehicleTrackingRaw?.w1BaseUrl === 'string' && vehicleTrackingRaw.w1BaseUrl
          ? vehicleTrackingRaw.w1BaseUrl
          : defaults.vehicleTrackingSettings.w1BaseUrl,
      weightraxBaseUrl:
        typeof vehicleTrackingRaw?.weightraxBaseUrl === 'string' && vehicleTrackingRaw.weightraxBaseUrl
          ? vehicleTrackingRaw.weightraxBaseUrl
          : defaults.vehicleTrackingSettings.weightraxBaseUrl,
      useANPR: Boolean(vehicleTrackingRaw?.useANPR ?? defaults.vehicleTrackingSettings.useANPR),
      arrivalThresholdKg:
        typeof vehicleTrackingRaw?.arrivalThresholdKg === 'number'
          ? vehicleTrackingRaw.arrivalThresholdKg
          : defaults.vehicleTrackingSettings.arrivalThresholdKg,
      stableWeightVariationKg:
        typeof vehicleTrackingRaw?.stableWeightVariationKg === 'number'
          ? vehicleTrackingRaw.stableWeightVariationKg
          : defaults.vehicleTrackingSettings.stableWeightVariationKg,
      stableReadingCount:
        typeof vehicleTrackingRaw?.stableReadingCount === 'number'
          ? vehicleTrackingRaw.stableReadingCount
          : defaults.vehicleTrackingSettings.stableReadingCount,
      axleWeightVariationKg:
        typeof vehicleTrackingRaw?.axleWeightVariationKg === 'number'
          ? vehicleTrackingRaw.axleWeightVariationKg
          : defaults.vehicleTrackingSettings.axleWeightVariationKg,
      secondsToWaitBetweenAxleChanges:
        typeof vehicleTrackingRaw?.secondsToWaitBetweenAxleChanges === 'number'
          ? vehicleTrackingRaw.secondsToWaitBetweenAxleChanges
          : defaults.vehicleTrackingSettings.secondsToWaitBetweenAxleChanges,
      secondsToWaitForDeparture:
        typeof vehicleTrackingRaw?.secondsToWaitForDeparture === 'number'
          ? vehicleTrackingRaw.secondsToWaitForDeparture
          : defaults.vehicleTrackingSettings.secondsToWaitForDeparture,
      secondsToHoldPlate:
        typeof vehicleTrackingRaw?.secondsToHoldPlate === 'number'
          ? vehicleTrackingRaw.secondsToHoldPlate
          : defaults.vehicleTrackingSettings.secondsToHoldPlate,
      docketLinkExpiryDays:
        typeof vehicleTrackingRaw?.docketLinkExpiryDays === 'number' && vehicleTrackingRaw.docketLinkExpiryDays > 0
          ? vehicleTrackingRaw.docketLinkExpiryDays
          : defaults.vehicleTrackingSettings.docketLinkExpiryDays,
      secondsBeforeShowingDocket:
        typeof vehicleTrackingRaw?.secondsBeforeShowingDocket === 'number'
          ? vehicleTrackingRaw.secondsBeforeShowingDocket
          : defaults.vehicleTrackingSettings.secondsBeforeShowingDocket,
      secondsToShowDocket:
        typeof vehicleTrackingRaw?.secondsToShowDocket === 'number' && vehicleTrackingRaw.secondsToShowDocket > 0
          ? vehicleTrackingRaw.secondsToShowDocket
          : defaults.vehicleTrackingSettings.secondsToShowDocket,
      addTotalToAxleWeights: Boolean(vehicleTrackingRaw?.addTotalToAxleWeights ?? defaults.vehicleTrackingSettings.addTotalToAxleWeights),
      axleGroupTotalLabel:
        typeof vehicleTrackingRaw?.axleGroupTotalLabel === 'string' && vehicleTrackingRaw.axleGroupTotalLabel
          ? vehicleTrackingRaw.axleGroupTotalLabel
          : defaults.vehicleTrackingSettings.axleGroupTotalLabel
    },
    deviceDefaults: {
      autoApprove: Boolean(deviceDefaultsRaw?.autoApprove ?? defaults.deviceDefaults.autoApprove)
    },
    securitySettings: {
      minPinLength:
        typeof securitySettingsRaw?.minPinLength === 'number' ? securitySettingsRaw.minPinLength : defaults.securitySettings.minPinLength,
      maxPinLength:
        typeof securitySettingsRaw?.maxPinLength === 'number' ? securitySettingsRaw.maxPinLength : defaults.securitySettings.maxPinLength
    },
    timeKeeperSettings: {
      checkIntervalMinutes:
        typeof timeKeeperSettingsRaw?.checkIntervalMinutes === 'number'
          ? timeKeeperSettingsRaw.checkIntervalMinutes
          : defaults.timeKeeperSettings.checkIntervalMinutes,
      ntpServers:
        Array.isArray(ntpServersRaw) && ntpServersRaw.every((server) => typeof server === 'string')
          ? (ntpServersRaw as string[])
          : defaults.timeKeeperSettings.ntpServers
    },
    keyboardSettings: {
      rows:
        Array.isArray(keyboardSettingsRaw?.rows) &&
        keyboardSettingsRaw.rows.every((r) => typeof r === 'string') &&
        keyboardSettingsRaw.rows.length > 0
          ? (keyboardSettingsRaw.rows as string[])
          : defaults.keyboardSettings.rows
    },
    visitFieldDefinitions: parseOptionalFieldDefinitions(source.visitFieldDefinitions),
    vehicleFieldDefinitions: parseOptionalFieldDefinitions(source.vehicleFieldDefinitions),
    materialFieldDefinitions: parseOptionalFieldDefinitions(source.materialFieldDefinitions),
    temperatureAlertSettings: {
      alertEmailTo: typeof temperatureAlertRaw?.alertEmailTo === 'string' ? temperatureAlertRaw.alertEmailTo : null
    },
    docketPrefix: typeof source.docketPrefix === 'string' ? source.docketPrefix : defaults.docketPrefix,
    gtnWeighMaterialId: typeof source.gtnWeighMaterialId === 'number' ? source.gtnWeighMaterialId : null,
    axleWeighMaterialId: typeof source.axleWeighMaterialId === 'number' ? source.axleWeighMaterialId : null,
    defaultCustomerId: typeof source.defaultCustomerId === 'number' ? source.defaultCustomerId : null
  };
}

export interface Site {
  siteId: number | null;
  siteKey: string;
  tenantId: number | null;
  street: string | null;
  suburb: string | null;
  cityId: number | null;
  state: string | null;
  countryId: number | null;
  lng: number | null;
  lat: number | null;
  companyId: number;
  businessUnitId: number | null;
  regionId: number | null;
  externalId: string | null;
  settings: string | null;
  siteSettings: SiteSettings;
  rv: string | null;
  createdDate: string | null;
  createdBy: number | null;
  modifiedDate: string | null;
  modifiedBy: number | null;

  // UI compatibility fields used by Site Management
  companyName: string;
  siteName: string;
  city: string | null;
  country: string | null;
  isActive: boolean;
}

export interface UpsertSiteRequest {
  companyId: number;
  siteName: string;
  siteKey?: string | null;
  street?: string | null;
  suburb?: string | null;
  cityId?: number | null;
  state?: string | null;
  countryId?: number | null;
  lng?: number | null;
  lat?: number | null;
  businessUnitId?: number | null;
  regionId?: number | null;
  externalId?: string | null;
  settings?: string | null;
  // Omit entirely (leave undefined) to preserve the site's existing settings on update.
  siteSettings?: SiteSettings | null;
  isActive: boolean;
  createdBy?: number;
  modifiedBy?: number;
}

const endpoints = {
  key: 'api/sites'
};

type SiteApiShape = Record<string, unknown>;

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');
const asNullableString = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};
const asNullableObject = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
};
const asNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) return Number(value);
  return null;
};
const asNumber = (value: unknown): number => {
  const parsed = asNullableNumber(value);
  return parsed ?? 0;
};
const asBoolean = (value: unknown): boolean => (typeof value === 'boolean' ? value : false);

const toSlug = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

function normalizeSite(raw: SiteApiShape): Site {
  const company = asNullableObject(raw.company ?? raw.Company);

  const siteName = asString(raw.siteName ?? raw.SiteName ?? raw.name ?? raw.locationName);
  const street = asNullableString(raw.street ?? raw.Street ?? raw.address ?? raw.addressLine1 ?? raw.address1);
  const cityName = asNullableString(raw.cityName ?? raw.CityName ?? raw.city ?? raw.City);
  const countryName = asNullableString(raw.countryName ?? raw.CountryName ?? raw.country ?? raw.Country);
  const suburb = asNullableString(raw.suburb ?? raw.Suburb ?? cityName);
  const state = asNullableString(raw.state ?? raw.State ?? raw.region ?? raw.stateName);
  const siteKey = asString(raw.siteKey ?? raw.SiteKey) || toSlug(siteName);
  const siteSettings = parseSiteSettings(raw.siteSettings ?? raw.SiteSettings);

  return {
    siteId: asNullableNumber(raw.siteId ?? raw.SiteId ?? raw.id ?? raw.locationId),
    siteKey,
    tenantId: asNullableNumber(raw.tenantId ?? raw.TenantId),
    street,
    suburb,
    cityId: asNullableNumber(raw.cityId ?? raw.CityId),
    state,
    countryId: asNullableNumber(raw.countryId ?? raw.CountryId),
    lng: asNullableNumber(raw.lng ?? raw.Lng),
    lat: asNullableNumber(raw.lat ?? raw.Lat),
    companyId: asNumber(raw.companyId ?? raw.CompanyId ?? raw.parentId ?? company?.companyId ?? company?.id),
    businessUnitId: asNullableNumber(raw.businessUnitId ?? raw.BusinessUnitId),
    regionId: asNullableNumber(raw.regionId ?? raw.RegionId),
    externalId: asNullableString(raw.externalId ?? raw.ExternalId),
    settings: asNullableString(raw.settings ?? raw.Settings),
    siteSettings,
    rv: asNullableString(raw.rv ?? raw.RV),
    createdDate: asNullableString(raw.createdDate ?? raw.CreatedDate),
    createdBy: asNullableNumber(raw.createdBy ?? raw.CreatedBy),
    modifiedDate: asNullableString(raw.modifiedDate ?? raw.ModifiedDate),
    modifiedBy: asNullableNumber(raw.modifiedBy ?? raw.ModifiedBy),

    companyName: asString(raw.companyName ?? company?.companyName ?? company?.name),
    siteName,
    city: cityName ?? suburb,
    country: countryName,
    isActive: asBoolean(raw.isActive ?? raw.IsActive)
  };
}

function toApiPayload(data: UpsertSiteRequest): Record<string, unknown> {
  const normalizedSiteName = data.siteName.trim();
  const normalizedStreet = asNullableString(data.street);
  const normalizedSuburb = asNullableString(data.suburb);
  const normalizedState = asNullableString(data.state);
  const normalizedSiteKey = asNullableString(data.siteKey) ?? toSlug(normalizedSiteName);

  const payload: Record<string, unknown> = {
    companyId: data.companyId,
    siteName: normalizedSiteName,
    siteKey: normalizedSiteKey,
    street: normalizedStreet,
    suburb: normalizedSuburb,
    cityId: data.cityId ?? null,
    state: normalizedState,
    countryId: data.countryId ?? null,
    lng: data.lng ?? null,
    lat: data.lat ?? null,
    businessUnitId: data.businessUnitId ?? null,
    regionId: data.regionId ?? null,
    externalId: asNullableString(data.externalId),
    settings: asNullableString(data.settings),

    // Compatibility fields in case backend still accepts older names.
    address: normalizedStreet,
    city: normalizedSuburb,
    region: normalizedState,
    isActive: data.isActive
  };

  // Only include siteSettings when explicitly provided — the backend preserves the site's
  // existing settings when this key is omitted, but overwrites them with {} if it's present.
  if (data.siteSettings != null) {
    payload.siteSettings = data.siteSettings;
  }

  if (typeof data.createdBy === 'number' && data.createdBy > 0) {
    payload.createdBy = data.createdBy;
  }

  if (typeof data.modifiedBy === 'number' && data.modifiedBy > 0) {
    payload.modifiedBy = data.modifiedBy;
  }

  return payload;
}

/**
 * Clears every cached site list and lookup for the current tenant.
 *
 * Keys are the request URL plus a tenant suffix, so a prefix match covers the full list, the
 * per-company lookup behind the header's site picker, and any single-site entries.
 *
 * The cached data is evicted (`undefined`), not merely marked for revalidation. These hooks set
 * `revalidateIfStale: false`, so a hook that later keys onto an entry which still holds data serves
 * it without refetching — and asking an entry with no mounted subscriber to revalidate does nothing,
 * because the fetcher lives with the hook. That combination is what left a newly added site missing
 * from the header picker until a reload. With no data present, the next subscriber has to fetch.
 */
export function revalidateSites() {
  // Errors are swallowed: callers await this after a successful write, and a refetch that fails
  // should not make a save that already succeeded look like it failed. The stale data is evicted
  // either way, so the next read still goes to the server.
  return mutate((key: unknown) => typeof key === 'string' && key.startsWith(endpoints.key), undefined, { revalidate: true }).catch(
    () => undefined
  );
}

export async function createSite(data: UpsertSiteRequest): Promise<Site> {
  const response = await axiosServices.post(endpoints.key, toApiPayload(data));
  await revalidateSites();
  return normalizeSite((response.data ?? {}) as SiteApiShape);
}

export async function updateSite(siteId: number, data: UpsertSiteRequest): Promise<Site> {
  const response = await axiosServices.put(`${endpoints.key}/${siteId}`, toApiPayload(data));
  // A rename, a move to another company, or a deactivation all change what the pickers should offer.
  await revalidateSites();
  return normalizeSite((response.data ?? {}) as SiteApiShape);
}

function normalizeSiteList(data: unknown): Site[] {
  if (Array.isArray(data)) {
    return data.map((item) => normalizeSite((item ?? {}) as SiteApiShape));
  }

  if (data && typeof data === 'object') {
    const objectData = data as Record<string, unknown>;
    if (Array.isArray(objectData.items)) {
      return objectData.items.map((item) => normalizeSite((item ?? {}) as SiteApiShape));
    }
  }

  return [];
}

function sitesUrl(isActive: boolean | null): string {
  const query = new URLSearchParams();
  if (isActive === null) query.set('isActive', '');
  else if (isActive === false) query.set('isActive', 'false');
  const suffix = query.toString();
  return suffix ? `${endpoints.key}?${suffix}` : endpoints.key;
}

export async function getSites(isActive: boolean | null = true): Promise<Site[]> {
  const response = await axiosServices.get(sitesUrl(isActive));
  return normalizeSiteList(response.data);
}

// Mirrors Weightrax.Contracts.Responses.SiteLookupResponse.
export interface SiteLookup {
  siteId: number;
  siteName: string;
  companyId: number;
}

function siteLookupUrl(companyId: number | null, isActive: boolean | null): string {
  const query = new URLSearchParams();
  if (companyId != null) query.set('companyId', String(companyId));
  if (isActive !== null) query.set('isActive', String(isActive));
  const suffix = query.toString();
  return suffix ? `${endpoints.key}/lookup?${suffix}` : `${endpoints.key}/lookup`;
}

/**
 * Id/name pairs for a site picker, narrowed to one company. Far lighter per row than the full site
 * list, which carries the settings JSON — worth the separate endpoint anywhere only labels are shown.
 * Pass a null companyId to skip the fetch.
 */
export function useGetSiteLookup(companyId: number | null, isActive: boolean | null = true) {
  const url = companyId == null ? null : siteLookupUrl(companyId, isActive);
  const { data, isLoading, error } = useSWR<SiteLookup[]>(useTenantScopedKey(url), () => fetcher(url!), {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  return useMemo(
    () => ({
      siteLookup: Array.isArray(data) ? data : [],
      siteLookupLoading: isLoading,
      // See useGetCompanies: an empty array only means "no sites" once the fetch has returned.
      siteLookupLoaded: data !== undefined,
      siteLookupError: error
    }),
    [data, error, isLoading]
  );
}

/** Shared, cache-backed site list for the selected tenant. */
export function useGetSites(isActive: boolean | null = true) {
  const url = sitesUrl(isActive);
  const { data, isLoading, error } = useSWR<unknown>(useTenantScopedKey(url), () => fetcher(url), {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  return useMemo(
    () => ({
      sites: normalizeSiteList(data),
      sitesLoading: isLoading,
      sitesError: error
    }),
    [data, error, isLoading]
  );
}

export async function getSite(siteId: number): Promise<Site> {
  const response = await axiosServices.get(`${endpoints.key}/${siteId}`);
  return normalizeSite((response.data ?? {}) as SiteApiShape);
}

export function useGetSite(siteId: number | null) {
  const url = siteId ? `${endpoints.key}/${siteId}` : null;
  const { data, isLoading, error } = useSWR<SiteApiShape>(useTenantScopedKey(url), () => fetcher(url!), {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  return {
    site: data ? normalizeSite(data) : null,
    siteLoading: isLoading,
    siteError: error
  };
}

export async function deleteSite(siteId: number): Promise<void> {
  await axiosServices.delete(`${endpoints.key}/${siteId}`);
  await revalidateSites();
}

export interface SiteSearchResult {
  siteId: number;
  siteName: string;
  isActive: boolean;
  cityId: number | null;
  cityName: string | null;
  countryId: number | null;
  countryName: string | null;
  companyId: number;
  companyName: string;
  tenantId: number;
}

export async function searchSites(searchText: string, isActive?: boolean): Promise<SiteSearchResult[]> {
  const query = new URLSearchParams({ searchText });
  if (isActive !== undefined) query.set('isActive', String(isActive));
  const response = await axiosServices.get<unknown>(`${endpoints.key}/search?${query}`);
  const data = response.data;
  if (!Array.isArray(data)) return [];
  return data.map((item) => {
    const raw = (item ?? {}) as Record<string, unknown>;
    return {
      siteId: asNumber(raw.siteId),
      siteName: asString(raw.siteName),
      isActive: asBoolean(raw.isActive),
      cityId: asNullableNumber(raw.cityId),
      cityName: asNullableString(raw.cityName),
      countryId: asNullableNumber(raw.countryId),
      countryName: asNullableString(raw.countryName),
      companyId: asNumber(raw.companyId),
      companyName: asString(raw.companyName),
      tenantId: asNumber(raw.tenantId)
    };
  });
}
